import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import ScrollToTop from './components/ScrollToTop';
// CookieBanner is lazy-loaded (deferred, not needed for first paint)
import { lazy, Suspense, useEffect } from 'react';
import { LazyMotion } from 'framer-motion';
import { initConsent } from './utils/consent';
import './App.css';

const loadMotionFeatures = () => import('./motionFeatures').then(m => m.default);

// Layouts
import PublicLayout from './layouts/PublicLayout';
import AdminLayout from './layouts/AdminLayout';

// Home chargée en priorité (pas de lazy — c'est la page initiale)
import Home from './pages/public/Home';

// Public Pages — lazy loaded (hors Home)
const Services        = lazy(() => import('./pages/public/Services'));
const Projects        = lazy(() => import('./pages/public/Projects'));
const ProjetEnVente   = lazy(() => import('./pages/public/ProjetEnVente'));
const About           = lazy(() => import('./pages/public/About'));
const Contact    = lazy(() => import('./pages/public/Contact'));
const Login      = lazy(() => import('./pages/Login'));
const Sign       = lazy(() => import('./pages/public/Sign'));
const MentionsLegales          = lazy(() => import('./pages/public/MentionsLegales'));
const PolitiqueConfidentialite = lazy(() => import('./pages/public/PolitiqueConfidentialite'));
const Partners   = lazy(() => import('./pages/public/Partners'));
const ActualitesList = lazy(() => import('./pages/public/Actualites').then(m => ({ default: m.ActualitesList })));
const ActualiteDetail = lazy(() => import('./pages/public/Actualites').then(m => ({ default: m.ActualiteDetail })));
const NotFound = lazy(() => import('./pages/public/NotFound'));

// Service Detail Pages — lazy loaded
const Construction = lazy(() => import('./pages/public/services/Construction'));
const Renovation   = lazy(() => import('./pages/public/services/Renovation'));
const Piscine      = lazy(() => import('./pages/public/services/Piscine'));
const Exterieur    = lazy(() => import('./pages/public/services/Exterieur'));
const Maconnerie   = lazy(() => import('./pages/public/services/Maconnerie'));
const Interieur    = lazy(() => import('./pages/public/services/Interieur'));

// Admin Pages — lazy loaded
const Dashboard    = lazy(() => import('./pages/admin/Dashboard'));
const Clients      = lazy(() => import('./pages/admin/Clients'));
const Leads        = lazy(() => import('./pages/admin/Leads'));
const Quotes       = lazy(() => import('./pages/admin/Quotes'));
const Invoices     = lazy(() => import('./pages/admin/Invoices'));
const AdminProjects = lazy(() => import('./pages/admin/Projects'));
const Worksites    = lazy(() => import('./pages/admin/Worksites'));
const WorksiteDetail = lazy(() => import('./pages/admin/WorksiteDetail'));
const News         = lazy(() => import('./pages/admin/News'));
const SEO          = lazy(() => import('./pages/admin/SEO'));
const Portal       = lazy(() => import('./pages/public/Portal'));
const Settings     = lazy(() => import('./pages/admin/Settings'));
const Employees    = lazy(() => import('./pages/admin/Employees'));
const Timesheets   = lazy(() => import('./pages/admin/Timesheets'));
const Planning     = lazy(() => import('./pages/admin/Planning'));
const Todos        = lazy(() => import('./pages/admin/Todos'));
const SpreadsheetPage = lazy(() => import('./pages/admin/SpreadsheetPage'));

// Standalone Pages
const Badge = lazy(() => import('./pages/Badge'));
const CookieBanner = lazy(() => import('./components/CookieBanner'));

// Fallback minimaliste pendant le chargement lazy
const PageLoader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
    <div style={{ width: 32, height: 32, border: '2px solid #006039', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

function App() {
  useEffect(() => {
    // Initialize consent-based loaders (PostHog, etc.)
    initConsent();
  }, []);

  return (
    <HelmetProvider>
    <LazyMotion features={loadMotionFeatures}>
    <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              {/* Service Detail Pages */}
              <Route path="/services/construction" element={<Construction />} />
              <Route path="/services/renovation"   element={<Renovation />} />
              <Route path="/services/piscine"       element={<Piscine />} />
              <Route path="/services/exterieur"     element={<Exterieur />} />
              <Route path="/services/maconnerie"    element={<Maconnerie />} />
              <Route path="/services/interieur"     element={<Interieur />} />
              {/* French Routes */}
              <Route path="/realisations"      element={<Projects />} />
              <Route path="/projets-en-vente"  element={<ProjetEnVente />} />
              <Route path="/a-propos"          element={<About />} />
              {/* Legacy English Routes (redirect) */}
              <Route path="/projects" element={<Navigate to="/realisations" replace />} />
              <Route path="/about"    element={<Navigate to="/a-propos" replace />} />
              <Route path="/contact"  element={<Contact />} />
              <Route path="/partenaires" element={<Partners />} />
              <Route path="/actualites" element={<ActualitesList />} />
              <Route path="/actualites/:slug" element={<ActualiteDetail />} />
              {/* Legacy English alias for news */}
              <Route path="/news" element={<ActualitesList />} />
              <Route path="/news/:slug" element={<ActualiteDetail />} />
              <Route path="/mentions-legales"          element={<MentionsLegales />} />
              <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
              {/* 404 — must stay inside PublicLayout to keep nav/footer */}
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Login */}
            <Route path="/login" element={<Login />} />

            {/* Badge — standalone, sans auth */}
            <Route path="/badge" element={<Badge />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index        element={<Dashboard />} />
              <Route path="clients"    element={<Clients />} />
              <Route path="quotes"     element={<Quotes />} />
              <Route path="invoices"   element={<Invoices />} />
              <Route path="leads"      element={<Leads />} />
              <Route path="projects"   element={<AdminProjects />} />
              <Route path="chantiers"  element={<Worksites />} />
              <Route path="chantiers/:id" element={<WorksiteDetail />} />
              <Route path="news"       element={<News />} />
              <Route path="seo"        element={<SEO />} />
              <Route path="employees"  element={<Employees />} />
              <Route path="timesheets" element={<Timesheets />} />
              <Route path="planning"   element={<Planning />} />
              <Route path="todos"      element={<Todos />} />
              <Route path="tableur"    element={<SpreadsheetPage />} />
              <Route path="settings"   element={<Settings />} />
            </Route>

            {/* Signature publique (standalone, sans nav) */}
            <Route path="/sign/:token" element={<Sign />} />

            {/* Espace client privé (magic link, standalone, sans nav) */}
            <Route path="/mon-espace/:token" element={<Portal />} />

            {/* Global fallback — NotFound is also matched inside PublicLayout for routes within it */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Suspense fallback={null}><CookieBanner /></Suspense>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
    </LazyMotion>
    </HelmetProvider>
  );
}

export default App;
